import React, { useEffect, useState } from "react";
import { useField } from "formik";
import { ErrorMessage, Flex, HelpIndicator, Label, Text, useDebouncedFn } from "@jsq/ds/core";
import { FormTextInput } from "@jsq/ds/forms";
import { Icon } from "@jsq/ds/icons";
import { PaymentsRoutingNumberType } from "#graphql/supergraphEnums";
import { useValidateRoutingNumberLazyQuery } from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import { FieldGroup } from "../../../../../common/components/Form/Layout/FieldGroup";
import { MultiInputFormRow } from "../../../../../common/components/Form/Layout/MultiInputFormRow";
import { SimpleFormRow } from "../../../../../common/components/Form/Layout/SimpleFormRow";
import { useConsolidatedPaymentPrefContext } from "../../../Context";
import { useGenericBankValidation } from "../../../hooks/use-generic-bank-validation";
import { BankCountryMismatchWarning } from "../../BankCountryMismatchWarning";
import {
  BacsPaymentPrefInputFields,
  BacsPaymentPrefInputSchema,
  type BacsPaymentPrefInputType,
} from "../constants";
import { stripSortCodeDashes } from "../utils";

export const BacsBankDetails: React.FC = () => {
  const [showBankValidatorError, setShowBankValidatorError] = useState(false);
  const [sortCodeField] = useField<string>(BacsPaymentPrefInputFields.SortCode);
  const { selectedBankCountry } = useConsolidatedPaymentPrefContext();

  const { handleClear, handleValidateCompleted } =
    useGenericBankValidation<BacsPaymentPrefInputType>({
      nameField: BacsPaymentPrefInputFields.BankName,
      validatedCountryField: BacsPaymentPrefInputFields.BankCountryValidatedCountry,
    });

  const [doValidateRoutingNumber] = useValidateRoutingNumberLazyQuery({
    onCompleted: (data) => {
      const currentSortCode = stripSortCodeDashes(sortCodeField.value ?? "");
      const responseSortCode = stripSortCodeDashes(
        data?.validateRoutingNumber?.routingNumber ?? "",
      );

      if (currentSortCode !== responseSortCode) {
        return;
      }

      const { bankName, bankAddress, isValid } = data?.validateRoutingNumber || {};
      handleValidateCompleted({ setShowBankValidatorError, bankName, bankAddress, isValid });
    },
    context: clientContext(),
  });

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  const debouncedValidateRoutingNumber = useDebouncedFn(doValidateRoutingNumber, 500);

  useEffect(() => {
    const stripped = stripSortCodeDashes(sortCodeField.value ?? "");

    if (!stripped) {
      handleClear();
      setShowBankValidatorError(false);
      return;
    }

    const isValid = BacsPaymentPrefInputSchema.pick([
      BacsPaymentPrefInputFields.SortCode,
    ]).isValidSync({
      [BacsPaymentPrefInputFields.SortCode]: sortCodeField.value,
    });

    if (isValid) {
      debouncedValidateRoutingNumber({
        variables: {
          validateRoutingNumberInput: {
            routingNumber: stripped,
            routingNumberType: PaymentsRoutingNumberType.GbSortCode,
          },
        },
      });
    } else {
      handleClear();
      setShowBankValidatorError(false);
    }
  }, [sortCodeField.value, debouncedValidateRoutingNumber, handleClear]);

  return (
    <FieldGroup title="Bank details">
      <SimpleFormRow>
        <Flex flexDirection="column">
          <Label>Bank country</Label>
          <Text variant="regular-md">United Kingdom</Text>
        </Flex>
      </SimpleFormRow>
      <MultiInputFormRow>
        <Flex flexDirection="column">
          <FormTextInput
            label={
              <HelpIndicator
                id="sort-code-help-indicator"
                placement="right"
                content="A sort code is a 6-digit number that identifies your bank and branch, usually formatted as XX-XX-XX."
              >
                <Text>
                  Sort code <Icon type="question-circle" size="small" variant="outline" />
                </Text>
              </HelpIndicator>
            }
            isFullWidth
            name={BacsPaymentPrefInputFields.SortCode}
            placeholder="e.g. 12-34-56"
          />
          {showBankValidatorError && <ErrorMessage>Unrecognized sort code</ErrorMessage>}
        </Flex>
        <FormTextInput
          label="Bank name"
          isFullWidth
          name={BacsPaymentPrefInputFields.BankName}
        />
      </MultiInputFormRow>
      <BankCountryMismatchWarning
        bankCountryName={selectedBankCountry?.name}
        validatedCountryField={BacsPaymentPrefInputFields.BankCountryValidatedCountry}
      />
    </FieldGroup>
  );
};
